'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';

const navLinks = [
  { label: 'Framework', href: '/sac-planning-framework' },
  { label: 'Knowledge Hub', href: '/knowledge-hub' },
  { label: 'Use Cases', href: '/use-cases' },
  { label: 'Architecture', href: '/technical-architecture' },
  { label: 'Explainer', href: '/explainer' },
];

export default function Header() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'header-backdrop border-b border-border' : 'bg-white border-b border-border'
      }`}
    >
      <div className="max-w-container mx-auto px-6 h-14 flex items-center justify-between">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-2 group" aria-label="SACplans Home">
          <AppLogo size={28} text="SACplans" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
          {navLinks?.map((link) => (
            <Link
              key={link?.href}
              href={link?.href}
              className={`nav-link text-sm font-medium transition-colors duration-200 pb-0.5 ${
                pathname === link?.href
                  ? 'text-foreground active'
                  : 'text-muted hover:text-foreground'
              }`}
            >
              {link?.label}
            </Link>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <Link
            href="/consulting-services"
            className={`btn-${pathname === '/consulting-services' ? 'primary' : 'outline'} text-sm`}
          >
            Consulting Services
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 p-2 -mr-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          <span
            className={`block w-5 h-px bg-foreground transition-transform duration-300 origin-center ${
              menuOpen ? 'translate-y-2 rotate-45' : ''
            }`}
          />
          <span
            className={`block w-5 h-px bg-foreground transition-opacity duration-300 ${
              menuOpen ? 'opacity-0' : ''
            }`}
          />
          <span
            className={`block w-5 h-px bg-foreground transition-transform duration-300 origin-center ${
              menuOpen ? '-translate-y-2 -rotate-45' : ''
            }`}
          />
        </button>
      </div>
      {/* Mobile menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          menuOpen ? 'max-h-96 border-t border-border' : 'max-h-0'
        } bg-white`}
      >
        <nav className="px-6 py-4 flex flex-col gap-1" aria-label="Mobile navigation">
          {navLinks?.map((link) => (
            <Link
              key={link?.href}
              href={link?.href}
              className={`py-2.5 text-sm font-medium border-b border-border last:border-0 ${
                pathname === link?.href ? 'text-foreground' : 'text-muted'
              }`}
            >
              {link?.label}
            </Link>
          ))}
          <Link
            href="/consulting-services"
            className="mt-3 btn-primary text-center text-sm"
          >
            Consulting Services
          </Link>
        </nav>
      </div>
    </header>
  );
}